import { Component, Fragment, h, Host, Method, Prop } from '@stencil/core';
import { ItemDetails, TimeSlotPicker, Translations } from '../../../redux/types';
import { Price, SelectStyle } from '../../../types/types';
import { ButtonAppearance, ButtonSize, ButtonStyle } from '../../atoms/rentware-button/types';
import { CalendarMode } from '../../atoms/rentware-date-select/utils';

@Component({
  tag: 'rentware-item-template',
  styleUrl: 'style.sass',
  shadow: false,
})
export class RentwareItemTemplate {
  @Prop() itemId: string;
  @Prop() itemDetails: ItemDetails;
  @Prop() isThisItemInCart: boolean;
  @Prop() onSubmit;
  @Prop() onCheckoutClick: () => void;
  @Prop() loading: boolean;
  @Prop() price: Price;
  @Prop() firstAvailableDate: Date;
  @Prop() lastAvailableDate: Date;

  @Prop() orderReference: string;
  @Prop() locale: string;
  @Prop() translations: Translations;
  @Prop() withTime: boolean;
  @Prop() withDateRange: boolean;
  @Prop() selectedVariationId?: string;
  @Prop() selectedMileageId?: string;
  @Prop() onPickUpFocus;
  @Prop() onDropOffFocus;
  @Prop() onVariationChange;
  @Prop() loadingPickUpDays: boolean;
  @Prop() loadingDropOffDays: boolean;
  @Prop() loadingPickUpHours: boolean;
  @Prop() loadingDropOffHours: boolean;

  @Prop() emptyDatePlaceholder: string;
  @Prop() pickUpDate: Date;
  @Prop() pickUpTimeHint: string;
  @Prop() pickUpTimePlaceholder: string;
  @Prop() pickUpTime: string;
  @Prop() pickUpHours: string[];
  @Prop() pickUpDaysAvailability: {};
  @Prop() onPickUpTimeChange: (string) => void;
  @Prop() onPickUpDayClick: (string) => void;
  @Prop() onPickUpMonthChange: (string) => void;

  @Prop() dropOffDate: Date;
  @Prop() dropOffTimeHint: string;
  @Prop() dropOffTimePlaceholder: string;
  @Prop() dropOffTime: string;
  @Prop() dropOffHours: string[];
  @Prop() dropOffDaysAvailability: {};
  @Prop() onDropOffTimeChange: (string) => void;
  @Prop() onDropOffDayClick: (string) => void;
  @Prop() onDropOffMonthChange: (string) => void;

  @Prop() timeSlotPicker: TimeSlotPicker;

  @Prop() headerTitle: string;
  @Prop() pickUpLabel: string;
  @Prop() dropOffLabel: string;
  @Prop() buttonText: string;
  @Prop() checkoutButtonText?: string;
  @Prop() onChangeAmountToBook;
  @Prop() amountToAdd: number;
  @Prop() notAvailable: boolean;
  @Prop() notAvailableTitle: string;
  @Prop() notAvailableText: string;
  @Prop() backToArticleText: string;
  @Prop() onMileageChange: (any) => void;
  @Prop() onResetCalendar: (day?: Date) => void;

  pickUpRef;
  dropOffRef;

  //todo: make this using event
  @Method()
  async closePickers() {
    this.pickUpRef.collapse();
    this.dropOffRef.collapse();
  }

  get globalStyle() {
    return (
      (!!this.orderReference && 'CalendarTemplateContainer CalendarTemplateContainerDimensions BgDark') || 'CalendarTemplateContainer CalendarTemplateContainerDimensions BgWhite'
    );
  }

  get buttonControlValues() {
    return (
      this.price.available && {
        id: this.itemDetails?.id,
        minQuantity: this.price?.quantity?.min,
        maxQuantity: this.price?.quantity?.max,
        availableQuantity: this.price?.quantity?.available,
        currentQuantity: this.amountToAdd,
        onChange: this.onChangeAmountToBook,
      }
    );
  }

  get buttonAppearance(): ButtonAppearance {
    return this.price.available
      ? {
          fullWidth: true,
          size: ButtonSize.XL,
        }
      : {
          style: ButtonStyle.Inverse,
          fullWidth: true,
          disabled: true,
          size: ButtonSize.XL,
        };
  }

  get showDropOffPicker(): boolean {
    return this.withDateRange && !this.timeSlotPicker;
  }

  get enableDropOffPicker(): boolean {
    if (!this.pickUpDate || this.showNotAvailableOverlay) {
      return false;
    }

    if (this.withTime && !this.pickUpTime) {
      return false;
    }

    return true;
  }

  get showNotAvailableOverlay(): boolean {
    const componentLoading = this.loading || this.loadingPickUpHours || this.loadingDropOffHours;

    return this.notAvailable && !componentLoading;
  }

  render() {
    return (
      <Host class={this.globalStyle}>
        <rentware-spinner style={this.loading ? {} : { display: 'none' }} withOverlay={true} />

        <rentware-cart-header headerTitle={this.headerTitle} showTimer={this.isThisItemInCart} />

        {(this.orderReference && (
          <rentware-item-in-cart
            itemId={this.itemId}
            itemDetails={this.itemDetails}
            isThisItemInCart={this.isThisItemInCart}
            onCheckoutClick={this.onCheckoutClick}
            checkoutButtonText={this.checkoutButtonText}
            headerTitle={this.headerTitle}
            backToArticleText={this.backToArticleText}
          />
        )) || (
          // date & time select
          <Fragment>
            <rentware-spacer size="L" />

            {this.itemDetails?.mileageOptions && (
              <Fragment>
                <rentware-select-mileage
                  selectStyle={SelectStyle.Default}
                  mileageOptions={this.itemDetails.mileageOptions}
                  available={this.itemDetails.available}
                  selectedMileageId={this.selectedMileageId}
                  onMileageChange={this.onMileageChange}
                />

                <rentware-spacer size="M" />
              </Fragment>
            )}

            {!!this.itemDetails?.variation && (
              <Fragment>
                <rentware-select
                  selectStyle={SelectStyle.Default}
                  label={this.itemDetails.variation.name}
                  value={this.selectedVariationId}
                  onValueChange={e => this.onVariationChange(e.detail.value)}
                  options={this.itemDetails.variation.items.map(item => {
                    return {
                      label: item.name,
                      value: item.id,
                      disabled: !item.maxQuantity && !item.quantityInCart,
                    };
                  })}
                />

                <rentware-spacer size="M" />
              </Fragment>
            )}

            <rentware-date-select
              ref={el => (this.pickUpRef = el)}
              translations={this.translations}
              loadingDays={this.loadingPickUpDays}
              loadingPickUpHours={this.loadingPickUpHours}
              loadingDropOffHours={this.loadingDropOffHours}
              label={this.pickUpLabel}
              emptyDatePlaceholder={this.emptyDatePlaceholder}
              locale={this.locale}
              withTime={this.withTime}
              withDateRange={this.withDateRange}
              onFocus={this.onPickUpFocus}
              // date
              bookingDate={this.pickUpDate}
              firstAvailableDate={this.firstAvailableDate}
              daysAvailability={this.pickUpDaysAvailability}
              onDayClick={this.onPickUpDayClick}
              onMonthChange={this.onPickUpMonthChange}
              // time
              bookingTime={this.pickUpTime}
              bookingTimeHint={this.pickUpTimeHint}
              bookingTimePlaceholder={this.pickUpTimePlaceholder}
              bookingHours={this.pickUpHours}
              onBookingTimeChange={this.onPickUpTimeChange}
              // drop off time
              endTime={this.dropOffTime}
              endTimeHint={this.dropOffTimeHint}
              endTimePlaceholder={this.dropOffTimePlaceholder}
              endHours={this.dropOffHours}
              onEndTimeChange={this.onDropOffTimeChange}
              // date slot
              timeSlotPicker={this.timeSlotPicker}
              showNotAvailableOverlay={this.showNotAvailableOverlay}
              onResetCalendar={this.onResetCalendar}
            />

            <rentware-spacer size="M" />

            {this.showDropOffPicker && (
              <Fragment>
                <rentware-date-select
                  ref={el => (this.dropOffRef = el)}
                  calendarMode={CalendarMode.SingleDayDropOff}
                  translations={this.translations}
                  loadingDays={this.loadingDropOffDays}
                  loadingDropOffHours={this.loadingDropOffHours}
                  label={this.dropOffLabel}
                  emptyDatePlaceholder={this.emptyDatePlaceholder}
                  locale={this.locale}
                  withTime={this.withTime}
                  withDateRange={this.withDateRange}
                  disabled={!this.enableDropOffPicker}
                  // date
                  bookingDate={this.dropOffDate}
                  firstAvailableDate={this.firstAvailableDate}
                  lastAvailableDate={this.lastAvailableDate}
                  monthAtStart={this.pickUpDate}
                  daysAvailability={this.dropOffDaysAvailability}
                  onDayClick={this.onDropOffDayClick}
                  onMonthChange={this.onDropOffMonthChange}
                  onFocus={this.onDropOffFocus}
                  // time
                  bookingTime={this.dropOffTime}
                  bookingTimeHint={this.dropOffTimeHint}
                  bookingTimePlaceholder={this.dropOffTimePlaceholder}
                  bookingHours={this.dropOffHours}
                  onBookingTimeChange={this.onDropOffTimeChange}
                />
                <rentware-spacer size="M" />
              </Fragment>
            )}

            {((!this.notAvailable || this.loading) && (
              <rentware-button
                appearance={this.buttonAppearance}
                buttonText={this.buttonText}
                controlValues={this.buttonControlValues}
                onClick={this.onSubmit}
                isWaiting={this.loading}
              />
            )) || (
              <div class="NotAvailableContainer">
                <svg width="22" height="20" viewBox="0 0 22 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M9.26795 2C10.0377 0.666667 11.9623 0.666667 12.7321 2L20.5263 15.5C21.2961 16.8333 20.3338 18.5 18.7942 18.5H3.20577C1.66617 18.5 0.70392 16.8333 1.47372 15.5L9.26795 2Z"
                    stroke-width="2"
                  />
                </svg>

                <div class="NotAvailableContainerInner">
                  <span class="Title">{this.notAvailableTitle}</span>
                  <span>{this.notAvailableText}</span>
                </div>
              </div>
            )}

            <rentware-spacer size="S" />

            <rentware-secure secureTitle={this.headerTitle} goBackText={this.backToArticleText} />
          </Fragment>
        )}
      </Host>
    );
  }
}
